import {
  GradientAnimationMetaData,
  TextAnimationMetaData,
  TranslateTextAnimationMetaData,
  ZoomAnimationMetaData,
} from '../animation.types';

/**
 * Active text animation presets.
 * Source of truth: studio-text-animation-presets.ts (this file).
 * See docs/studio-text-animation.md for the full spec schema.
 */
export const TEXT_ANIMATION_PRESETS: Record<string, TextAnimationMetaData[]> = {
  'translate:bottom-by:word': [
    {
      duration: 1.25,
      translate: [0, 40],
      hold: 0.4,
      by: 'word',
      item: { overlap: 0.4 },
    } as TranslateTextAnimationMetaData,
  ],

  'translate:top-by:word': [
    {
      duration: 1.25,
      translate: [0, -40],
      hold: 0.4,
      by: 'word',
      item: { overlap: 0.4 },
    } as TranslateTextAnimationMetaData,
  ],

  'translate:bottom-by:letter': [
    {
      duration: 1.25,
      translate: [0, 40],
      hold: 0.5,
      by: 'letter',
      item: { overlap: 0.88 },
    } as TranslateTextAnimationMetaData,
  ],

  'translate:top-by:letter': [
    {
      duration: 1.25,
      translate: [0, -40],
      hold: 0.5,
      by: 'letter',
      item: { overlap: 0.88 },
    } as TranslateTextAnimationMetaData,
  ],

  'zoom:in-by:word': [
    {
      duration: 1.25,
      zoom: [0.6, 1],
      hold: 0.4,
      by: 'word',
      item: { overlap: 0.4 },
    } as ZoomAnimationMetaData,
  ],

  'zoom:in-by:letter': [
    {
      duration: 1.25,
      zoom: [0.6, 1],
      hold: 0.5,
      by: 'letter',
      item: { overlap: 0.88 },
    } as ZoomAnimationMetaData,
  ],

  'mask:bottom': [
    {
      duration: 1.25,
      translate: [0, 40],
      mask: true,
      item: {},
    } as TranslateTextAnimationMetaData,
  ],

  'mask:bottom-by:word': [
    {
      duration: 1.25,
      translate: [0, 40],
      mask: true,
      hold: 0.4,
      by: 'word',
      item: { overlap: 0.4 },
    } as TranslateTextAnimationMetaData,
  ],

  // ── Segment presets ─────────────────────────────────────────────────────────
  // Two metas, chained via .next.
  // Meta 0: first word zooms in; numberOfWords: 1, segmentMode: 'override'.
  // Meta 1: rest of words stagger by word.
  //   override → segment starts at endOfPrev (independent windows)
  //   additive → segment starts at 0 (dynamic reflow)

  '_segment:override': [
    {
      duration: 1.25,
      zoom: [0.6, 1],
      numberOfWords: 1,
      segmentMode: 'override',
    } as ZoomAnimationMetaData,
    {
      duration: 1.25,
      zoom: [0.6, 1],
      // number of words is undefined → "the rest"
      segmentMode: 'override',
    } as unknown as ZoomAnimationMetaData,
  ],
  '_segment:override:word': [
    {
      duration: 1.25,
      zoom: [0.6, 1],
      numberOfWords: 1,
      segmentMode: 'override',
      by: 'word',
      item: { overlap: 0.4 },
    } as ZoomAnimationMetaData,
    {
      duration: 1.25,
      segmentMode: 'override',
      zoom: [0.6, 1],
      by: 'word',
      item: { overlap: 0.4 },
    } as unknown as ZoomAnimationMetaData,
  ],
  'segment:override:word:dynamic': [
    {
      duration: 1.25,
      zoom: [0.6, 1],
      numberOfWords: 5,
      segmentMode: 'override',
      by: 'word',
      item: { overlap: 0.4 },
    } as ZoomAnimationMetaData,
    {
      duration: 1.25,
      segmentMode: 'override',
      // zoom: [0.8, 1],
      align: 'dynamic',
      by: 'word',
      item: { overlap: 0.8 },
    } as unknown as ZoomAnimationMetaData,
  ],
  // 'segment:override:word': [
  //   {
  //     duration: 1.25,
  //     zoom: [0.6, 1],
  //     numberOfWords: 1,
  //     segmentMode: 'override',
  //     item: {},
  //   } as ZoomAnimationMetaData,
  // ],
  '_segment:extend': [
    {
      duration: 1.25,
      zoom: [0.6, 1],
      numberOfWords: 1,
      segmentMode: 'override',
      item: {},
    } as ZoomAnimationMetaData,
    {
      duration: 1.25,
      by: 'word',
      segmentMode: 'additive',
      item: { overlap: 0.4 },
    } as unknown as ZoomAnimationMetaData,
  ],
  '_gradient:word': [
    {
      wordIndex: 0,
      gradient: ['#ffffff', '#7d3cff', '#ff3cac', '#ffffff'],
    } as GradientAnimationMetaData,
  ],
  'word:align': [
    {
      by: 'word',
      item: { overlap: 0 },
    } as TranslateTextAnimationMetaData,
  ],
  'word:align:continuous': [
    {
      by: 'word',
      duration: 1.3,
      item: { overlap: 0 },
      align: 'continuous',
    } as TranslateTextAnimationMetaData,
  ],
  // 'word:align:continuous:zoom': [
  //   {
  //     by: 'word',
  //     duration: 1.3,
  //     item: { overlap: 0 },
  //     align: 'continuous',
  //     zoom: [0.3, 1],
  //   } as any,
  // ],
};

export const TEXT_ANIMATION_PRESET_KEYS = Object.keys(
  TEXT_ANIMATION_PRESETS,
  // ).filter(key => key.startsWith('xxsegment') || key.startsWith('gradient'));
).filter(key => !key.startsWith('_'));

export const LAYOUT_ANIMATION_OPTIONS = [
  {
    label: 'Fixed',
    key: 'fixed',
  },
  {
    label: 'Replace',
    key: 'replace',
  },
  {
    label: 'Shift',
    key: 'shift',
  },
  {
    label: 'Carousel',
    key: 'carousel',
  },
];

// -- // -- // -- //

const textOptions = ['fade', 'word', 'gradient-mask', 'word+translate'];
// later word + zoom

export const TEXT_ANIMATION_OPTIONS = [
  {
    label: 'Translate',
    key: 'appear:line:translate',
    options: [
      {
        key: 'bottom',
        label: 'Bottom',
        options: textOptions,
      },
      {
        key: 'top',
        label: 'Top',
        options: textOptions,
      },
      {
        key: 'left',
        label: 'Left',
        options: textOptions,
      },
      {
        key: 'right',
        label: 'Right',
        options: textOptions,
      },
    ],
  },
  {
    label: 'Zoom',
    key: 'appear:line:zoom',
    options: [
      {
        key: 'in',
        label: 'In',
        options: ['fade', 'h-mask', 'v-mask', 'align+mask', 'word'],
      },
      {
        key: 'out',
        label: 'Out',
        options: ['fade', 'h-mask', 'v-mask', 'align+mask', 'word'],
      },
    ],
  },
  {
    label: 'Word',
    key: 'appear:word',
    options: [
      {
        key: 'align:none',
        label: 'No align',
        options: ['fade', 'left', 'zoom', 'blur', 'letter'],
      },
      {
        key: 'align:continuous',
        label: 'Continuous',
        options: ['zoom', 'blur', 'letter'],
      },
      {
        key: 'align:discrete',
        label: 'Discrete',
        options: ['zoom', 'blur', 'letter'],
      },
      {
        key: 'show-only-one-word',
        label: 'One word',
      },
    ],
  },
  {
    label: 'Letter',
    key: 'appear:letter',
    options: [
      {
        key: 'align:none',
        label: 'No align',
        options: ['cursor', 'move', 'zoom', 'blur'],
      },
      {
        key: 'align:continuous',
        label: 'Continuous',
        options: ['cursor', 'move', 'zoom', 'blur'],
      },
    ],
  },
];
